import { Component, ComponentRef, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Order } from '@app/services/order/order.model';
import { OrderService } from '@app/services/order/order.service';
import { PrintService } from '@app/services/print/print.service';
import { ShopService } from '@app/services/shops/shop.service';
import { ScannedOrderMultipleResults } from '@pages/workspace/fulfill/components/fulfill-side-panel-scan/components/scan-multiple-options/scan-multiple-options';
import { ToastService } from '@storybook/services/toast/toast.service';
import { Subject } from 'rxjs';
import { finalize, first, takeUntil } from 'rxjs/operators';
import { FulfillService } from '../../fulfill.service';
import * as utils from '../../utils';
import { PanelState, ScanResponse } from '../../utils/models';
import { ApiService } from '@app/core/services/api/api.service';

@Component({
  selector: 'fulfill-side-panel-scan',
  templateUrl: './fulfill-side-panel-scan.component.html',
  styleUrls: ['./fulfill-side-panel-scan.component.scss'],
  standalone: false,
})
export class FulfillSidePanelScanComponent implements OnInit, OnDestroy {
  @Input() label: string;
  @ViewChild('scannedOrderMultipleResults', { read: ViewContainerRef }) scannedOrderMultipleResults;
  private scannedOrder;
  private destroy$ = new Subject();
  private activeBatch;
  orderMultipleResultsRef: ComponentRef<ScannedOrderMultipleResults>;
  isLoading;
  searchString: string; // order number or dt.shipping id
  shopMetadata;
  private isCustomFulfillment: boolean = false;

  usePaginatedFulfillment = this.fulfillService.usePaginatedFulfillment;
  constructor(
    private fulfillService: FulfillService,
    private router: Router,
    private route: ActivatedRoute,
    private shopService: ShopService,
    private orderService: OrderService,
    private toastService: ToastService,
    private printService: PrintService,
    private apiService: ApiService,
  ) {}

  ngOnInit(): void {
    this.fulfillService.activeBatch$.pipe(takeUntil(this.destroy$)).subscribe((activeBatch) => {
      if (activeBatch && this.scannedOrder) {
        this.activeBatch = activeBatch;
        this.fulfillService.setActiveOrder(this.scannedOrder);
        this.fulfillService.setPanelState(PanelState.ORDER);
        this.scannedOrder = null;
      }
    });

    this.shopService.shop$.pipe(takeUntil(this.destroy$)).subscribe((shop) => {
      this.shopMetadata = shop?.metadata;
    });
    this.fulfillService.isCustomFulfillmentScanningViewEnabled$.subscribe((value: boolean) => {
      this.isCustomFulfillment = value;
    });
  }

  ngOnDestroy() {
    this.scannedOrder = null;
    this.activeBatch = null;
  }

  setOrderId(searchString) {
    // scan split order number escape / char
    this.searchString = searchString.replace(/\//g, '-');
  }

  closePanel() {
    this.fulfillService.togglePanel(false);
  }

  scanOrderV2() {
    this.isLoading = true;
    this.scannedOrder = null;
    this.router.navigate(['.'], { relativeTo: this.route, queryParams: { from: 'scan' } });

    const { forceScanBy } = this.shopMetadata as any;
    const identifier = this.searchString.replace(/ /g, '');
    const scanBy: 'shippingId' | 'orderNumber' =
      forceScanBy === 'label' ? 'shippingId' : forceScanBy === 'order' ? 'orderNumber' : null;

    this.searchString = '';

    this.fulfillService
      .scanOrderInBatches(scanBy, identifier)
      .pipe(first())
      .subscribe({
        next: (response) => {
          const orders = response?.orders;
          if (!orders?.length) {
            this.toastService.showToast({ message: `No orders were found for #${identifier}`, mode: 'danger' });
            this.isLoading = false;
            if (this.orderMultipleResultsRef) {
              this.orderMultipleResultsRef.destroy();
              this.orderMultipleResultsRef = null;
            }
            return;
          }

          if (orders.length === 1) {
            this.getTargetOrderInfo(orders[0]);
            return;
          }

          this.createMultipleOrderSelectionComponent(
            orders.map((order) => ({ ...order, batchName: order?.orderBatch?.name })),
          );
        },
        error: () => {
          this.isLoading = false;
          this.toastService.showToast({ message: 'Failed to scan order', mode: 'danger' });
        },
      });
  }

  scanOrder() {
    if (!this.searchString) {
      return;
    }

    if (this.usePaginatedFulfillment()) {
      this.scanOrderV2();
      return;
    }

    this.isLoading = true;
    this.scannedOrder = null;

    this.router.navigate(['.'], { relativeTo: this.route, queryParams: { from: 'scan' } });
    const { forceScanBy } = this.shopMetadata as any; // temp force scanning hot fix
    const scanResponse: ScanResponse = utils.findOrderInBatches(
      this.searchString,
      this.fulfillService.batches,
      forceScanBy,
    );

    this.searchString = '';

    if (scanResponse.status === 'failed') {
      this.toastService.showToast({ message: scanResponse.message, mode: 'danger' });
      this.isLoading = false;
      if (this.orderMultipleResultsRef) {
        this.orderMultipleResultsRef.destroy();
        this.orderMultipleResultsRef = null;
      }
      return;
    }

    if (scanResponse.status === 'succeeded') {
      if (scanResponse.orders?.length == 1) {
        this.getTargetOrderInfo(scanResponse.orders[0]);
        return;
      }

      // more than one option
      return this.createMultipleOrderSelectionComponent(scanResponse.orders);
    }

    this.isLoading = false;
  }

  createMultipleOrderSelectionComponent(orders: Order[]) {
    this.isLoading = false;
    if (!this.orderMultipleResultsRef) {
      this.orderMultipleResultsRef = this.scannedOrderMultipleResults.createComponent(ScannedOrderMultipleResults);
      this.orderMultipleResultsRef.instance.orders = orders;
      this.orderMultipleResultsRef.instance.onClose.pipe(first()).subscribe(() => {
        this.orderMultipleResultsRef.destroy();
        this.orderMultipleResultsRef = null;
      });

      this.orderMultipleResultsRef.instance.onSelect.pipe(first()).subscribe((order) => {
        this.isLoading = true;
        this.getTargetOrderInfo(order);
      });
    }
  }

  getTargetOrderInfo(order) {
    this.orderService
      .getOrderObs(order.id)
      .pipe(
        first(),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe(
        (order) => {
          const batch = this.usePaginatedFulfillment()
            ? order.orderBatch
            : utils.findBatchById(order.orderBatch?.id, this.fulfillService.batches);
          this.scannedOrder = order;
          if (!this.usePaginatedFulfillment()) {
            this.fulfillService.setActiveBatch2({ ...batch });
          }
          this.fulfillService.setActiveBatch(batch);
          this.fulfillService.onScanOrder.next(order);
          if (this.isCustomFulfillment) {
            this.printService.printOrder('openLabelForOrder', [order.id]);
          }
        },
        (error) => this.toastService.showToast({ message: 'Cannot find this order', mode: 'danger' }),
      );
  }
}
